import React, { useEffect } from "react";
import { HomeOutlined, RightOutlined } from "@ant-design/icons";
import { DashboardLayout } from "../../../components/layout/components/DashboardLayout";
import { Navbar } from "../../../components/layout/components/Navbar";
import { useGetUserInfo } from "../../../hooks/useGetUserInfo";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import { useGetSingleJob } from "../hooks/useGetSingleJob";
import { useGetSectors } from "../hooks/useGetSectors";
import { routePath } from "../../../config/routeMgt/routePath";
import { JobDetailsShimmer } from "../../../components/layout/components/loaders/Shimmer";
import { formatDate } from "../../../admin/Authentication/hooks/useDateFormat";
import { useLikeJob } from "../hooks/useLikeJob";
import { useGetTalentCount } from "features/home/hooks/useGetTalentCount";
import { useQueryClient } from "react-query";
import { QUERY_KEY_FOR_JOBS } from "../hooks/useGetJobs";
import { notification } from "antd";
import { formatJobYearsLabel } from "../utils/formatJobYearsLabel";
import { DEFAULT_OG_IMAGE } from "../../../constants/general";
import { useMetaPixelForTrackedJobPage } from "../hooks/useMetaPixelForTrackedJob";
import { trackMetaJobLeadForListing } from "../../../utils/metaPixelJob";
import { useProfileCompletion } from "../../../features/settings/hooks/useProfileCompletion";

export const JobDetails = () => {
  const { token, userInfo } = useGetUserInfo();
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id as number | string;
  useMetaPixelForTrackedJobPage(
    id !== undefined && id !== null ? String(id) : undefined,
  );
  const { data: sectorData } = useGetSectors();
  const { data, isLoading, isFetching, refetch } = useGetSingleJob({
    token,
    id: id as any,
  });
  const { mutate: likeMutate, isLoading: isApplying } = useLikeJob();

  const queryClient = useQueryClient();
  const { data: profileData } = useGetTalentCount();

  const { incompleteSections, isLoading: isProfileLoading } =
    useProfileCompletion();
  const isProfileComplete =
    incompleteSections.length === 0 && !isProfileLoading;

  const sector = sectorData?.find((item) => item.id === data?.sector_id);

  const formattedDate = formatDate(data?.expiry_date);
  const dateCreated = formatDate(data?.created_at);

  const hasApplied = !!data?.has_liked;
  const isProfileIncomplete = !isProfileComplete;
  const isApplyDisabled = hasApplied || isProfileIncomplete || isApplying;

  const getButtonStyles = () => {
    if (hasApplied) {
      return "bg-green-100 text-green-700 cursor-not-allowed";
    }
    if (isProfileIncomplete) {
      return "bg-blue-100 text-blue-600 cursor-not-allowed";
    }
    return "bg-[#00094A] text-white hover:bg-[#00094A]/90 active:scale-95";
  };

  const getButtonText = () => {
    if (hasApplied) return "Applied";
    if (isProfileIncomplete) return "Complete your Profile to Apply";
    return "Apply Now";
  };

  const getButtonTitle = () => {
    if (hasApplied) return "You have already applied for this job";
    if (isProfileIncomplete)
      return `Complete your profile to apply (Missing: ${incompleteSections.join(", ")})`;
    return "Click to apply";
  };

  const stripHtmlTags = (html: string) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").substring(0, 200);
  };

  const formatDescription = (html: string): string => {
    if (!html) return "";

    const jobSummaryIndex = html.indexOf("Job Summary");
    if (jobSummaryIndex !== -1) {
      html = html.substring(jobSummaryIndex);
    }

    let decodedHtml = html
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, " ");

    if (
      decodedHtml.includes("<ul") &&
      decodedHtml.includes("<li") &&
      decodedHtml.includes("</ul>")
    ) {
      return decodedHtml;
    }

    let plainText = decodedHtml
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<p>/gi, "")
      .replace(/<strong>/gi, "")
      .replace(/<\/strong>/gi, "")
      .replace(/<b>/gi, "")
      .replace(/<\/b>/gi, "")
      .replace(/<[^>]*>/g, "")
      .replace(/\u00A0/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim();

    if (!plainText) {
      return html;
    }

    let sentences: string[] = [];

    let tempText = plainText;
    tempText = tempText.replace(/Q\d+\./g, (match) =>
      match.replace(/\./g, "___DOT___"),
    );

    const parts = tempText.split(/(?<=\.)\s+/);

    for (let part of parts) {
      part = part.replace(/___DOT___/g, ".");
      if (part.trim()) {
        sentences.push(part.trim());
      }
    }

    if (sentences.length <= 1 && plainText.includes(". ")) {
      const manualParts = plainText.split(". ");
      for (let part of manualParts) {
        if (part.trim()) {
          let cleanPart = part.trim();
          if (
            !cleanPart.endsWith(".") &&
            !cleanPart.endsWith("?") &&
            !cleanPart.endsWith("!")
          ) {
            cleanPart += ".";
          }
          sentences.push(cleanPart);
        }
      }
    }

    if (sentences.length === 0) {
      sentences = [plainText];
    }

    const result: string[] = [];
    let inList = false;
    let listType: "bullet" | "check" | "number" = "bullet";

    const isHeader = (line: string): boolean => {
      const headerPatterns = [
        /^Job Summary$/i,
        /^Job Overview$/i,
        /^Key Responsibilities$/i,
        /^Principal Duties and Responsibilities$/i,
        /^Responsibilities$/i,
        /^Duties$/i,
        /^Qualifications$/i,
        /^Required Qualifications$/i,
        /^Preferred Qualifications$/i,
        /^Education$/i,
        /^Experience$/i,
        /^Skills$/i,
        /^Key Skills$/i,
        /^Competencies$/i,
        /^Attributes$/i,
        /^Knowledge$/i,
        /^Key Performance Indicators$/i,
        /^Minimum Qualifications$/i,
        /^Other Requirements$/i,
        /^Language$/i,
        /^Working Conditions$/i,
        /^Benefits$/i,
        /^About the Role$/i,
        /^What You'll Do$/i,
        /^What You'll Need$/i,
        /^Requirements$/i,
        /^Personal Attributes$/i,
        /^Job Type$/i,
        /^Department$/i,
        /^Reports To$/i,
        /^Location$/i,
        /^Fundamentals of Software Testing/i,
        /^API Testing/i,
        /^Performance Testing/i,
        /^Database Management/i,
        /^Web Automation/i,
        /^Programming Language/i,
        /^Behavioral Questions/i,
        /^Competency$/i,
        /^Competency Details$/i,
      ];

      const trimmed = line.trim();
      return (
        headerPatterns.some((pattern) => pattern.test(trimmed)) ||
        (line.length < 50 &&
          /^[A-Z][a-z]+(\s+[A-Z][a-z]+)*$/.test(line) &&
          !/^[•·✓☑Q\d]/.test(line))
      );
    };

    const isListItem = (line: string): boolean => {
      return (
        /^[•·▪◦○●◆■\-*+✓☑☐☒✅]\s/.test(line) ||
        /^\d+\.\s/.test(line) ||
        /^[a-zA-Z]\)\s/.test(line) ||
        /^Q\d+\./.test(line)
      );
    };

    const getListType = (line: string): "bullet" | "check" | "number" => {
      if (/^[✓☑☐☒✅]\s/.test(line)) return "check";
      if (/^\d+\.\s/.test(line) || /^Q\d+\./.test(line)) return "number";
      return "bullet";
    };

    const cleanListItem = (line: string): string => {
      let cleaned = line
        .replace(/^[•·▪◦○●◆■\-*+]\s*/, "")
        .replace(/^[✓☑☐☒✅]\s*/, "")
        .replace(/^Q\d+\.\s*/, "")
        .replace(/^\d+\.\s*/, "")
        .replace(/^[a-zA-Z]\)\s*/, "")
        .trim();
      return cleaned;
    };

    const hasColonPattern = (line: string): boolean => {
      return line.includes(":") && !isListItem(line) && !isHeader(line);
    };

    const splitColonText = (
      line: string,
    ): { key: string; value: string } | null => {
      const colonIndex = line.indexOf(":");
      if (colonIndex === -1) return null;
      const key = line.substring(0, colonIndex).trim();
      const value = line.substring(colonIndex + 1).trim();
      if (key.length < 40 && value.length > 0) {
        return { key, value };
      }
      return null;
    };

    for (let i = 0; i < sentences.length; i++) {
      let line = sentences[i].trim();
      if (!line) continue;

      if (
        /^(Job Description Database|Position:|Reports to:|Grade Level:|Supervises:)/i.test(
          line,
        )
      ) {
        continue;
      }

      if (isListItem(line)) {
        const type = getListType(line);
        const content = cleanListItem(line);

        if (inList && listType !== type) {
          result.push("</ul>");
          inList = false;
        }

        if (!inList) {
          let listClass = "list-disc pl-6 mb-3 space-y-1";
          if (type === "check") listClass = "list-none pl-0 mb-3 space-y-1";
          else if (type === "number")
            listClass = "list-decimal pl-6 mb-3 space-y-1";
          result.push(`<ul class="${listClass}">`);
          inList = true;
          listType = type;
        }

        if (type === "check") {
          result.push(
            `<li class="flex items-start gap-2 text-gray-700"><span class="text-green-600 font-bold">✓</span> ${content}</li>`,
          );
        } else {
          result.push(
            `<li class="text-gray-700 leading-relaxed">${content}</li>`,
          );
        }
        continue;
      }

      if (inList) {
        result.push("</ul>");
        inList = false;
      }

      if (isHeader(line)) {
        const cleanLine = line.replace(/^[#*]+\s*/, "").trim();
        if (cleanLine.length < 30 && /^[A-Z]/.test(cleanLine)) {
          result.push(
            `<h5 class="font-semibold mt-4 mb-2 text-gray-700">${cleanLine}</h5>`,
          );
        } else {
          result.push(
            `<h4 class="font-semibold mt-5 mb-3 text-gray-800 border-b border-gray-200 pb-2">${cleanLine}</h4>`,
          );
        }
        continue;
      }

      if (hasColonPattern(line)) {
        const colonData = splitColonText(line);
        if (colonData) {
          if (
            !/^(Job Description Database|Position|Reports to|Grade Level|Supervises)/i.test(
              colonData.key,
            )
          ) {
            result.push(
              `<p class="mb-2 text-gray-700 leading-relaxed"><span class="font-semibold text-gray-800">${colonData.key}:</span> ${colonData.value}</p>`,
            );
          }
          continue;
        }
      }

      const qMatch = line.match(/^(Q\d+\.)\s*(.+)/);
      if (qMatch) {
        result.push(
          `<p class="mb-2 text-gray-700 leading-relaxed"><span class="font-semibold text-gray-800">${qMatch[1]}</span> ${qMatch[2]}</p>`,
        );
        continue;
      }

      if (line.length < 3) continue;

      result.push(`<p class="mb-2 text-gray-700 leading-relaxed">${line}</p>`);
    }

    if (inList) {
      result.push("</ul>");
    }

    if (result.length === 0) {
      return html;
    }

    return result.join("\n");
  };

  const handleApplyNow = () => {
    if (!data?.id || !token) return;

    if (isProfileIncomplete) {
      notification.error({
        message: "Profile Incomplete",
        description: `Please complete your profile before applying for jobs. Missing: ${incompleteSections.join(", ")}`,
        duration: 5,
      });
      return;
    }

    if (hasApplied) {
      notification.info({
        message: "Already Applied",
        description: "You have already applied to this job.",
        duration: 3,
      });
      return;
    }

    likeMutate(
      {
        ...data,
        token,
        has_liked: true,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEY_FOR_JOBS]);

          refetch();
          trackMetaJobLeadForListing({
            id: data?.id,
            slug: data?.slug,
            title: data?.title,
          });
          notification.success({
            message: "Application Successful!",
            description: `You have successfully applied for "${data?.title}" at ${data?.company_name}.`,
            duration: 4,
          });
        },
        onError: (error) => {
          console.error("Failed to apply:", error);
          notification.error({
            message: "Application Failed",
            description:
              "There was an error submitting your application. Please try again.",
            duration: 4,
          });
        },
      },
    );
  };

  const handleBookmark = () => {
    if (!data?.id) return;
    console.log("Bookmark functionality to be implemented");
  };

  useEffect(() => {
    if (data) {
      const updateMetaTag = (property: string, content: string) => {
        let meta = document.querySelector(`meta[property="${property}"]`);
        if (!meta) {
          meta = document.createElement("meta");
          meta.setAttribute("property", property);
          document.head.appendChild(meta);
        }
        meta.setAttribute("content", content);
      };

      const updateTwitterTag = (name: string, content: string) => {
        let meta = document.querySelector(`meta[name="${name}"]`);
        if (!meta) {
          meta = document.createElement("meta");
          meta.setAttribute("name", name);
          document.head.appendChild(meta);
        }
        meta.setAttribute("content", content);
      };

      updateMetaTag("og:title", `${data.title} at ${data.company_name}`);
      updateMetaTag(
        "og:description",
        stripHtmlTags(data.description || data.jobsummary || "").substring(
          0,
          200,
        ),
      );
      updateMetaTag("og:image", DEFAULT_OG_IMAGE);
      updateMetaTag("og:url", window.location.href);
      updateMetaTag("og:type", "website");
      updateTwitterTag("twitter:card", "summary_large_image");
      updateTwitterTag(
        "twitter:title",
        `${data.title} at ${data.company_name}`,
      );
      updateTwitterTag(
        "twitter:description",
        stripHtmlTags(data.description || data.jobsummary || "").substring(
          0,
          200,
        ),
      );
      updateTwitterTag("twitter:image", DEFAULT_OG_IMAGE);
    }
  }, [data]);

  if (isLoading || isFetching) {
    return <JobDetailsShimmer />;
  }

  return (
    <DashboardLayout>
      <Navbar title="Jobs" description="View and Manage Jobs" />

      <div>
        <h3 className="text-sm flex items-center gap-1 text-gray-700">
          <HomeOutlined className="text-gray-500" />
          <NavLink to={routePath.jobs}>
            <span className="text-sm ">Jobs&nbsp;</span>
          </NavLink>
          <RightOutlined className="text-xs" />
          <span>&nbsp;Job Details</span>
          <RightOutlined className="text-xs" />
          <span>&nbsp;{`${data?.title} (${data?.company_name})`}</span>
        </h3>
      </div>

      <div>
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 mb-4 mt-4">
          <div className="bg-white rounded-lg shadow-md h-full p-6 lg:col-span-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-base font-bold">{data?.title}</h3>
                <h4 className="inline-flex items-center gap-1.5 mt-2 bg-[#7F56D9] text-white px-2.5 py-1 rounded-full text-xs font-semibold">
                  <i className="ri-building-4-line text-white text-sm"></i>
                  {data?.company_name}
                </h4>
              </div>
              <div className="flex items-center gap-4">
                {userInfo?.recruiter && (
                  <button
                    disabled={!data?.id}
                    onClick={() =>
                      navigate(routePath.viewApplicants(data?.id).path)
                    }
                    className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    View Applicants
                  </button>
                )}

                {!userInfo?.recruiter && (
                  <button
                    type="button"
                    disabled={isApplyDisabled}
                    onClick={handleApplyNow}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${getButtonStyles()}`}
                    title={getButtonTitle()}
                  >
                    {isApplying ? "Applying..." : getButtonText()}
                  </button>
                )}
              </div>
            </div>

            {/* Job status badge */}
            {data?.status !== "draft" && !data?.expired && (
              <div className="mt-3">
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                  Open
                </span>
              </div>
            )}

            <div className="relative">
              <div>
                <ul className="p-2 divide-y divide-gray-200 w-full">
                  <li className="grid grid-cols-1 sm:grid-cols-4 sm:divide-x sm:divide-gray-200/70 py-4">
                    <div className="flex flex-col w-full py-1 pl-6 text-sm font-semibold">
                      <div className="flex items-center space-x-2 text-gray-800">
                        <span className="rounded-full w-[32px] h-[32px] bg-gray-100 flex items-center justify-center">
                          <i className="ri-map-pin-line text-lg text-blue-500"></i>
                        </span>
                        <div className="flex flex-col">
                          <div className="text-gray-400 text-xs">Location</div>
                          <div className="text-gray-500 text-base capitalize">{`${data?.location_city}, ${data?.location_country}`}</div>
                        </div>
                      </div>
                    </div>

                    {/* Employment Type */}
                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-gray-100 flex items-center justify-center">
                        <i className="ri-briefcase-line text-lg text-blue-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">
                          Employment Type
                        </div>
                        <div className="text-gray-500 text-base capitalize">
                          {data?.employment_type}
                        </div>
                      </div>
                    </div>

                    {/* Work Model */}
                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-gray-100 flex items-center justify-center">
                        <i className="ri-building-line text-lg text-green-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">Work Model</div>
                        <div className="text-gray-500 text-base capitalize">
                          {data?.type}
                        </div>
                      </div>
                    </div>

                    {/* Experience Required */}
                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-purple-50 flex items-center justify-center">
                        <i className="ri-award-line text-lg text-purple-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">
                          Years of experience
                        </div>
                        <div className="text-gray-500 text-base capitalize">
                          {formatJobYearsLabel(data?.years_of_experience) ||
                            "—"}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="grid grid-cols-1 sm:grid-cols-4 sm:divide-x sm:divide-gray-200/70 py-4">
                    {/* Industry */}
                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-blue-50 flex items-center justify-center">
                        <i className="ri-briefcase-4-line text-lg text-blue-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">Industry</div>
                        <div className="text-gray-500 text-base capitalize">
                          {sector?.name}
                        </div>
                      </div>
                    </div>

                    {/* Job Status */}
                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-green-50 flex items-center justify-center">
                        <i className="ri-checkbox-circle-line text-lg text-green-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">Job Status</div>
                        <div className="text-gray-500 text-base capitalize">
                          {data?.status}
                        </div>
                      </div>
                    </div>

                    {/* Date Posted */}
                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-orange-50 flex items-center justify-center">
                        <i className="ri-calendar-event-line text-lg text-orange-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">Date Posted</div>
                        <div className="text-gray-500 text-base capitalize">
                          {dateCreated}
                        </div>
                      </div>
                    </div>
                    {/* Application Deadline */}
                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-orange-50 flex items-center justify-center">
                        <i className="ri-calendar-event-line text-lg text-orange-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">
                          Application Deadline
                        </div>
                        <div className="text-gray-500 text-base capitalize">
                          {formattedDate}
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md h-full p-6 lg:col-span-6">
            <div className="relative">
              <div>
                <div className="flex flex-col w-full py-1 pl-6 text-sm font-semibold">
                  <div className="flex items-center space-x-2 text-gray-800">
                    <div className="flex flex-col w-full">
                      <div className="text-gray-600 text-base capitalize mb-3">
                        Job Description
                      </div>
                      <div
                        className="job-description-content"
                        dangerouslySetInnerHTML={{
                          __html: formatDescription(data?.description || ""),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md h-full p-6 lg:col-span-6">
            <div className="relative">
              <div>
                <div className="flex flex-col w-full py-1 pl-6 text-sm font-semibold">
                  <div className="flex items-center space-x-2 text-gray-800">
                    <div className="flex flex-col">
                      <div className="text-gray-600 text-base">Competency</div>
                      <div className="flex flex-wrap gap-4 mt-3 capitalize">
                        {Array.isArray(data?.skills) &&
                          data?.skills.map((skill: any, index: number) => {
                            const skillName =
                              typeof skill === "string" ? skill : skill.name;
                            return (
                              <span
                                key={
                                  typeof skill === "string" ? index : skill.id
                                }
                                className="bg-[#E3E5FF] text-[#4B5563] text-xs font-medium px-4 py-3 rounded-md capitalize"
                              >
                                {skillName}
                              </span>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
